import axios from "axios";
import _Cookies from "universal-cookie";
import { getAcessToken, setCookie, getRefreshToken } from "@helper/cookie";
import { COOKIE_JWT } from "@helper/constant";
import { redirectLogout } from "@helper/common";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export default class BaseService {
    static API_URL_ROOT = process.browser ? process.env.NEXT_PUBLIC_API : process.env.API;
    static API_AUTH_REFRESH_TOKEN = "auth/refresh-token";

    static convertApiErrData(data) {
        if (data) {
            let { errors: errArr } = data;
            if (errArr && errArr.length) {
                let errors = [];
                errArr.forEach((err) => {
                    errors = errors.concat(err.messages || []);
                });
                Object.assign(data, { errors });
            }
        }
        return data;
    }

    constructor(ctx) {
        this._evtKey = "" + new Date().getTime();
        this._ctx = ctx;
    }

    _sessionExpiredTimer = null;

    refreshAuthData(response) {
        let _self = this;
        return new Promise((resolve, reject) => {
            if (response) {
                let { status, message } = response || {};
                if (401 === status && message === "jwt expired") {
                    isRefreshing = true;
                    let authData = getRefreshToken(_self._ctx) || {};
                    if (!Object.keys(authData).length) {
                        return resolve(null);
                    }
                    let { refreshToken, remember = false } = authData;
                    return resolve(
                        _self
                            .post('auth/refresh-token', { refreshToken, remember })
                            .then((data) => {
                                let dataToken = JSON.stringify(data);
                                setCookie(COOKIE_JWT, dataToken);
                                processQueue(null, data.accessToken);
                                return data;
                            })
                            .catch((err) => {
                                processQueue(err, null);
                                return null
                            })
                    );
                } else {
                    reject(response);
                }
            }
            return resolve(null);
        });
    }

    _request(_config) {
        let self = this;
        let accessToken = getAcessToken(self._ctx);
        let config = Object.assign(
            {
                baseURL: BaseService.API_URL_ROOT + "/",
            },
            (_config = _config || {}),
            accessToken
                ? {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
                : {},
        );

        let incomming = new Promise((resolve, reject) => {
            return axios(config)
                .then((response) => {

                    if (config.responseType && config.responseType === "blob") {
                        return response;
                    }
                    let { data: apiData } = response.data;
                    // apiData = Object.assign(apiData || {}),{ _: () => response };
                    // apiData = Object.assign(apiData || {});
                    return apiData;
                })
                .then(resolve)
                .catch((err) => {
                    let originalRequest = err.config;
                    let { response } = err;
                    if (!response) {
                        throw err;
                    }
                    let { data: apiData = {}, status } = response;
                    apiData = Object.assign(apiData, { status });
                    if (!apiData.status) return reject('Vui lòng kiểm tra lại kết nối.')

                    //Nếu lỗi 501
                    if (apiData.status == 501 || apiData.status == 400 || apiData.status == 404) {
                        return reject(apiData);
                    }

                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        })
                            .then((token) => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                self._request(originalRequest);
                            })
                            .catch((err) => err);
                    }

                    if (
                        originalRequest.headers.Authorization !==
                        `Bearer ${getAcessToken(self._ctx)}`
                    ) {
                        originalRequest.headers.Authorization = `Bearer ${getAcessToken(
                            self._ctx
                        )}`;
                        return self._request(originalRequest);
                    }

                    this.refreshAuthData(apiData)
                        .then((data) => {
                            if (!data) {
                                if (!self._ctx) {
                                    return redirectLogout()
                                }
                                else {
                                    return reject('Error refresh token')
                                }
                            }
                            isRefreshing = false;
                            resolve(self._request(config));
                        })
                        .catch(reject);
                });
        });
        return incomming;
    }

    get(url, params, _config) {
        let config = Object.assign(
            {
                method: "get",
                url,
                params,
            },
            {
                ..._config,
                cancelToken: this.cancelTokenSource.token
            } ||
            {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    post(url, data, _config) {
        let config = Object.assign(
            {
                url,
                method: "post",
                data,
            },
            { ..._config, cancelToken: this.cancelTokenSource.token } || {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    postFormData(url, formData, _config) {
        let self = this;
        let accessToken = getAcessToken(self._ctx);
        let config = Object.assign(
            {
                url,
                method: "post",
                data: formData,
                baseURL: BaseService.API_URL_ROOT + "/",
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
                },
            },
            { ..._config, cancelToken: this.cancelTokenSource.token } || {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    put(url, data, _config) {
        let config = Object.assign(
            {
                url,
                method: "put",
                data,
            },
            { ..._config, cancelToken: this.cancelTokenSource.token } || {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    patch(url, data, _config) {
        let config = Object.assign(
            {
                url,
                method: "patch",
                data,
            },
            { ..._config, cancelToken: this.cancelTokenSource.token } || {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    delete(url, params, _config) {
        let config = Object.assign(
            {
                url,
                method: "delete",
                params,
            },
            { ..._config, cancelToken: this.cancelTokenSource.token } || {
                cancelToken: this.cancelTokenSource.token,
            }
        );
        return this._request(config);
    }

    file(url, params, _config) {
        let config = Object.assign(
            {
                responseType: "blob",
            },
            _config
        );
        return this.get(url, params, config);
    }

    cancelTokenSource = axios.CancelToken.source();
    cancelRequest = () => {
        this.cancelTokenSource.cancel();
    };

    getErrorMessage = (error, msg = "Đã có lỗi xảy ra. Vui lòng F5 thử lại") => {
        if (typeof error === "string") return error;

        if (error instanceof Array) {
            let objErr = {}
            error.forEach((err) => {
                objErr[err.key] = err.msg;
            });
            return JSON.stringify(objErr);
        }

        let _message = "";

        if (typeof error === 'object') {
            let { message = '', errmsg = '' } = error || {}
            _message = message || msg || errmsg;
        } else {
            _message = msg;
        }
        return _message;
    };
}
const _static = BaseService;